/**
 * AI Model Configurations
 * Central source of truth for all AI model IDs and metadata (fal.ai + Replicate)
 */

/**
 * Model group definitions
 */
export const MODEL_GROUPS = {
  'best-quality': {
    label: 'Best quality',
    order: 1
  },
  'high-quality': {
    label: 'High quality',
    order: 2
  },
  affordable: {
    label: 'Fast and affordable',
    order: 3
  }
};

/**
 * All available AI models with metadata. Token costs must match the server
 * table (public/functions/replicate-models.js); the two are drift-guarded by
 * test/generator/image-models-sync.test.js.
 */
export const REPLICATE_MODELS = {
  // Best quality
  'nano-banana-pro': {
    name: 'Nano Banana Pro',
    type: 'replicate',
    group: 'best-quality',
    logo: '/ui_assets/model-google.png',
    version: '99256cc418d9ac41854575e2f1c8846ce2defd0c0fb6ff2d5cbc3c826be75bc8',
    estimatedTime: 60,
    includeIn4x: true,
    // Default output tier, and the paid tiers above it (#1887). The server
    // (public/functions/replicate-models.js) prices them; drift-guarded.
    resolution: '2K',
    outputTiers: { '4K': { tokenCost: 5 } },
    tokenCost: 3
  },
  'fal-flux-3-edit': {
    name: 'Flux 3',
    type: 'fal',
    group: 'best-quality',
    logo: '/ui_assets/model-black-forest-labs.png',
    endpoint: 'blackforestlabs/flux-3/edit-image',
    resolution: '2k',
    estimatedTime: 80,
    // Too slow for the 4x batch; Flux 2 Klein is the Flux there.
    includeIn4x: false,
    tokenCost: 2,
    requiresSourceImage: true,
    // fal rejects FLUX.3 reference images over 4 megapixels.
    maxInputMegapixels: 4
  },

  // High quality
  'seedream-5-pro': {
    name: 'Seedream 5.0 Pro',
    type: 'replicate',
    group: 'high-quality',
    logo: '/ui_assets/model-bytedance.png',
    modelName: 'bytedance/seedream-5-pro',
    resolution: '2K',
    estimatedTime: 30,
    includeIn4x: false,
    tokenCost: 2
  },
  'nano-banana-2': {
    name: 'Nano Banana 2',
    type: 'replicate',
    group: 'high-quality',
    logo: '/ui_assets/model-google.png',
    modelName: 'google/nano-banana-2',
    resolution: '2K',
    estimatedTime: 30,
    includeIn4x: false,
    tokenCost: 2
  },

  // Fast and affordable (on trial)
  'fal-flux-2-klein-9b-edit': {
    name: 'Flux 2 Klein',
    type: 'fal',
    group: 'affordable',
    logo: '/ui_assets/model-black-forest-labs.png',
    endpoint: 'fal-ai/flux-2/klein/9b/edit',
    estimatedTime: 15,
    includeIn4x: true,
    tokenCost: 1,
    requiresSourceImage: true,
    // Output matches the input size; capping the input keeps cost and render
    // time down.
    maxInputMegapixels: 2
  },
  'grok-imagine-image-2': {
    name: 'Grok Imagine 2.0',
    type: 'replicate',
    group: 'affordable',
    logo: '/ui_assets/model-xai.png',
    modelName: 'xai/grok-imagine-image-2',
    resolution: '2k',
    estimatedTime: 20,
    includeIn4x: true,
    tokenCost: 1
  },
  'fal-muse-image-edit': {
    name: 'Muse Image',
    type: 'fal',
    group: 'affordable',
    logo: '/ui_assets/model-meta.png',
    endpoint: 'meta/muse-image/edit',
    estimatedTime: 20,
    includeIn4x: true,
    tokenCost: 1,
    requiresSourceImage: true
  }
};

/**
 * Output resolutions a model offers, default first: [] when it has no
 * selectable tiers (#1887).
 * @param {Object} model - REPLICATE_MODELS entry
 * @returns {string[]}
 */
export const getOutputTiers = (model) =>
  model?.outputTiers && model.resolution
    ? [model.resolution, ...Object.keys(model.outputTiers)]
    : [];

/**
 * Token cost of one render at an output tier; an unknown or default tier
 * costs the model's base tokenCost.
 * @param {Object} model - REPLICATE_MODELS entry
 * @param {string|null} [tier]
 * @returns {number}
 */
export const getTierTokenCost = (model, tier) =>
  (tier && model?.outputTiers?.[tier]?.tokenCost) || model?.tokenCost || 1;

/**
 * Get model name by version ID
 * @param {string} version - The model version ID
 * @returns {string} The human-readable model name
 */
export const getModelNameByVersion = (version) => {
  const model = Object.values(REPLICATE_MODELS).find(
    (m) => m.version === version
  );
  return model?.name || 'AI Model';
};

/**
 * Get model configuration by version ID
 * @param {string} version - The model version ID
 * @returns {Object|null} The model configuration object
 */
export const getModelByVersion = (version) => {
  return (
    Object.values(REPLICATE_MODELS).find((m) => m.version === version) || null
  );
};

/**
 * Default model ID key
 */
export const DEFAULT_REPLICATE_MODEL_ID = 'nano-banana-pro';

/**
 * Default model version (Nano Banana Pro)
 */
export const DEFAULT_REPLICATE_MODEL_VERSION =
  REPLICATE_MODELS['nano-banana-pro'].version;

/**
 * Get models grouped by their group property
 * @returns {Object} Object with group keys and arrays of model entries
 */
export const getGroupedModels = () => {
  const grouped = {};

  // Initialize groups
  Object.keys(MODEL_GROUPS).forEach((groupKey) => {
    grouped[groupKey] = [];
  });

  // Group models
  Object.entries(REPLICATE_MODELS).forEach(([modelId, modelConfig]) => {
    const group = modelConfig.group;
    if (group && grouped[group]) {
      grouped[group].push({
        id: modelId,
        ...modelConfig
      });
    }
  });

  return grouped;
};

/**
 * Video model group definitions
 */
export const VIDEO_MODEL_GROUPS = {
  'video-best-quality': {
    label: 'Best quality',
    order: 1
  },
  'video-high-quality-fast': {
    label: 'High quality and fast',
    order: 2
  },
  'video-versatile': {
    label: 'Versatile',
    order: 3
  }
};

/**
 * Video models configuration
 */
export const VIDEO_MODELS = {
  'kwaivgi/kling-v3-video': {
    name: 'Kling v3.0 Pro',
    type: 'replicate',
    group: 'video-best-quality',
    logo: '/ui_assets/model-kling.png',
    estimatedTime: 330,
    tokenCost5s: 20,
    tokenCost10s: 40
  },
  'google/veo-3.1': {
    name: 'Veo 3.1',
    type: 'replicate',
    group: 'video-best-quality',
    logo: '/ui_assets/model-google.png',
    estimatedTime: 120,
    // Veo only supports 4s/8s, so the 5s/10s cost tiers bill 4s/8s
    shortDuration: 4,
    tokenCost5s: 20,
    tokenCost10s: 40
  },
  'google/veo-3.1-fast': {
    name: 'Veo 3.1 Fast',
    type: 'replicate',
    group: 'video-high-quality-fast',
    logo: '/ui_assets/model-google.png',
    estimatedTime: 90,
    // Veo only supports 4s/8s, so the 5s/10s cost tiers bill 4s/8s
    shortDuration: 4,
    tokenCost5s: 10,
    tokenCost10s: 20
  },
  'google/veo-3.1-lite': {
    name: 'Veo 3.1 Lite',
    type: 'replicate',
    group: 'video-high-quality-fast',
    logo: '/ui_assets/model-google.png',
    estimatedTime: 60,
    // Veo only supports 4s/8s, so the 5s/10s cost tiers bill 4s/8s
    shortDuration: 4,
    tokenCost5s: 5,
    tokenCost10s: 10
  },
  'bytedance/seedance-2.0-fast': {
    name: 'Seedance 2.0 Fast',
    type: 'replicate',
    group: 'video-high-quality-fast',
    logo: '/ui_assets/model-bytedance.png',
    estimatedTime: 90,
    tokenCost5s: 15,
    tokenCost10s: 30
  },
  'lightricks/ltx-2.5-fast': {
    name: 'LTX-2.5 Fast',
    type: 'replicate',
    group: 'video-high-quality-fast',
    logo: '/ui_assets/model-lightricks.png',
    estimatedTime: 40,
    tokenCost5s: 6,
    tokenCost10s: 12
  },
  'wan-video/wan-2.7-i2v': {
    name: 'Wan 2.7 I2V',
    type: 'replicate',
    group: 'video-versatile',
    logo: '/ui_assets/model-wan.png',
    estimatedTime: 100,
    tokenCost5s: 15,
    tokenCost10s: 30
  }
};

/**
 * Get video models grouped by their group property
 * @returns {Object} Object with group keys and arrays of model entries
 */
export const getGroupedVideoModels = () => {
  const grouped = {};

  // Initialize groups
  Object.keys(VIDEO_MODEL_GROUPS).forEach((groupKey) => {
    grouped[groupKey] = [];
  });

  // Group models
  Object.entries(VIDEO_MODELS).forEach(([modelId, modelConfig]) => {
    const group = modelConfig.group;
    if (group && grouped[group]) {
      grouped[group].push({
        id: modelId,
        ...modelConfig
      });
    }
  });

  return grouped;
};
